import Image from "next/image";
import { BackgroundImage } from "@/components/Primitives/BackgroundImage";
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";

import dynamic from "next/dynamic";
import Link from "next/link";
const AnimatedSection = dynamic(() => import("../Animations/useAnimations"), {
  ssr: false,
});
const ScreenAwareContent = dynamic(
  () => import("../../utils/screenAwareContent"),
  {
    ssr: false,
    loading: () => <div className="min-h-screen h-screen" />,
  }
);

interface DirectusInterfaceData {
  heading?: {
    text?: string;
  };
  subheading?: {
    text?: string;
  };
  text?: {
    text?: string;
  };
  images?: {
    images_id: {
      alt: string;
      image_file: {
        image: {
          id: string;
          filename_download: string;
        };
      };
    };
  }[];
}

export const fields = [
  "heading.text",

  "subheading.text",

  "text.text",

  "images.images_id.alt",
  "images.images_id.image_file.image.id",
  "images.images_id.image_file.image.filename_download",
] as const;

interface MeetDoctorProps {
  data?: DirectusInterfaceData;
  zIndex: number;
  thresholds: number[][];
  transitionTime?: number;
  mobileThresholds: number[][];
  helperRotation?: number;
}

export default function MeetDoctor({
  data,
  zIndex,
  thresholds,
  transitionTime,
  mobileThresholds,
  helperRotation,
}: MeetDoctorProps) {
  const backgroundImage = data?.images?.[0];
  const awardImages = data?.images?.slice(1);

  return (
    <ScreenAwareContent
      supportedContent={
        <AnimatedSection
          helperRotation={helperRotation}
          className="h-screen overflow-hidden fixed w-full pt-16 xl:pt-32  top-0 left-0"
          thresholds={[
            thresholds?.[0]?.[0],
            thresholds?.[thresholds.length - 1]?.[1],
          ]}
          mobileThresholds={[
            mobileThresholds?.[0]?.[0],
            mobileThresholds?.[mobileThresholds.length - 1]?.[1],
          ]}
          zIndex={zIndex}
          withZIndex={true}
          transitionTime={transitionTime}
          variants={{
            desktop: {
              visible: { x: 0, y: 0, opacity: 1 },
              above: { x: 0, y: "100vh", opacity: 1 },
              below: { x: "-100vw", y: 0, opacity: 1 },
            },
            mobile: {
              visible: { x: 0, y: 0, opacity: 1 },
              above: { x: "-100vw", y: 0, opacity: 1 },
              below: { x: 0, y: 0, opacity: 0 },
            },
          }}
        >
          <div className="h-screen overflow-hidden relative w-full">
            <div className="absolute left-0 top-0 inset-0 w-full h-full lg:translate-y-0 -translate-y-[10%] z-10">
              <AnimatedSection
                section="div"
                className="h-full w-[200vw] lg:w-full -ml-[50vw] lg:ml-0"
                mobileThresholds={[
                  mobileThresholds?.[1]?.[0],
                  mobileThresholds?.[1]?.[1],
                ]}
                variants={{
                  desktop: {
                    visible: { opacity: 1, scale: 1, y: 0 },
                    above: { opacity: 1, scale: 1, y: 0 },
                    below: { opacity: 1, scale: 1, y: 0 },
                  },
                  mobile: {
                    visible: { opacity: 1, scale: 0.7, y: "-20vh" },
                    above: { opacity: 1, scale: 1.2, y: 0 },
                    below: { opacity: 1, scale: 0.7, y: "-20vh" },
                  },
                }}
              >
                <BackgroundImage
                  assetId={
                    backgroundImage?.images_id?.image_file?.image?.id || ""
                  }
                  temp={
                    backgroundImage?.images_id?.image_file?.image?.id &&
                    backgroundImage?.images_id?.image_file?.image
                      ?.filename_download
                      ? getReadableImageUrl(
                          backgroundImage.images_id.image_file.image.id,
                          backgroundImage.images_id.image_file.image
                            .filename_download
                        )
                      : "/images/8.jpg"
                  }
                  className="object-cover object-center"
                  quality={100}
                  priority
                />
              </AnimatedSection>
            </div>
            <AnimatedSection
              zIndex={10}
              withZIndex={true}
              section="div"
              className="relative h-full z-30"
              mobileThresholds={[
                mobileThresholds?.[2]?.[0],
                mobileThresholds?.[2]?.[1],
              ]}
              variants={{
                desktop: {
                  visible: { opacity: 1, y: 0 },
                  above: { opacity: 1, y: 0 },
                  below: { opacity: 1, y: 0 },
                },
                mobile: {
                  visible: { opacity: 1, y: "-20vh" },
                  above: { opacity: 1, y: 0 },
                  below: { opacity: 1, y: "-30vh" },
                },
              }}
            >
              <AnimatedSection
                section="div"
                className="absolute left-0 top-0 w-full h-full inset-0 lg:hidden z-20"
                mobileThresholds={[
                  mobileThresholds?.[1]?.[0],
                  mobileThresholds?.[1]?.[1],
                ]}
                variants={{
                  desktop: {
                    visible: { opacity: 1, x: 0 },
                    above: { opacity: 1, x: 0 },
                    below: { opacity: 1, x: 0 },
                  },
                  mobile: {
                    visible: { opacity: 1, x: 0 },
                    above: { opacity: 1, x: "-200vw" },
                    below: { opacity: 1, x: 0 },
                  },
                }}
              >
                <div className="absolute z-10 top-[15%] left-[-100%] w-[300%] h-[150%] bg-white rounded-full" />
              </AnimatedSection>
              <AnimatedSection
                section="div"
                className="relative h-full z-30"
                mobileThresholds={[
                  mobileThresholds?.[1]?.[0],
                  mobileThresholds?.[1]?.[1],
                ]}
                variants={{
                  desktop: {
                    visible: { opacity: 1, x: 0, y: 0 },
                    above: { opacity: 1, x: 0, y: 0 },
                    below: { opacity: 1, x: 0, y: 0 },
                  },
                  mobile: {
                    visible: { opacity: 1, x: 0, y: 0 },
                    above: { opacity: 1, x: "-200vw", y: 0 },
                    below: { opacity: 1, x: 0, y: 0 },
                  },
                }}
              >
                <AnimatedSection
                  section="div"
                  className="gl-container flex flex-col lg:items-start h-full"
                  mobileThresholds={[
                    mobileThresholds?.[2]?.[0],
                    mobileThresholds?.[2]?.[1],
                  ]}
                  variants={{
                    desktop: {
                      visible: { opacity: 1, x: 0, y: 0 },
                      above: { opacity: 1, x: 0, y: 0 },
                      below: { opacity: 1, x: 0, y: 0 },
                    },
                    mobile: {
                      visible: { opacity: 1, x: 0, y: "5vh" },
                      above: { opacity: 1, x: 0, y: 0 },
                      below: { opacity: 1, x: 0, y: "20vh" },
                    },
                  }}
                >
                  <div className="lg:w-[32%] h-full md:flex items-center">
                    <div className="mx-auto lg:mx-0 lg:max-w-2xl space-y-7 lg:space-y-9 relative">
                      <div className="mt-32">
                        <div className="space-y-3 pb-3">
                          <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary">
                            {data?.heading?.text || "Meet Dr. Bader"}
                          </h2>
                          <p className="text-primary/90 font-greycliff text-base lg:text-2xl leading-loose tracking-tight">
                            {data?.subheading?.text ||
                              "Facial Plastic Surgeon in Plano"}
                          </p>
                        </div>
                        <div
                          className="text-[1rem] font-normal leading-normal font-greycliff tracking-tight text-primary [&>p]:py-2"
                          dangerouslySetInnerHTML={sanitizeHTML(
                            data?.text?.text || ""
                          )}
                        />
                        <div className="mt-4 justify-center flex">
                          <Link
                            href="/about/meet-dr-bader/"
                            className="w-full sm:w-auto px-12 py-3 bg-primary rounded-[3rem] text-white text-xl font-normal font-greycliff text-center"
                          >
                            Meet Dr. Bader
                          </Link>
                        </div>
                      </div>
                    </div>
                    <AnimatedSection
                      section="div"
                      className="w-full flex items-center justify-between lg:w-auto lg:absolute lg:bottom-[30%] lg:right-32"
                      mobileThresholds={[
                        mobileThresholds?.[2]?.[0],
                        mobileThresholds?.[2]?.[1],
                      ]}
                      variants={{
                        desktop: {
                          visible: { opacity: 1, x: 0 },
                          above: { opacity: 1, x: 0 },
                          below: { opacity: 1, x: 0 },
                        },
                        mobile: {
                          visible: { opacity: 1, x: 0 },
                          above: { opacity: 1, x: 0, y: "-150vh" },
                          below: { opacity: 1, x: 0 },
                        },
                      }}
                    >
                      {awardImages?.map((award, index) => (
                        <div
                          key={index}
                          className={`flex items-center ${
                            index > 0 ? "lg:ml-16" : ""
                          }`}
                        >
                          <Image
                            src={
                              award.images_id.image_file.image.id &&
                              award.images_id.image_file.image.filename_download
                                ? getReadableImageUrl(
                                    award.images_id.image_file.image.id,
                                    award.images_id.image_file.image
                                      .filename_download
                                  )
                                : ""
                            }
                            alt={award.images_id.alt}
                            width={100}
                            height={70}
                            className="object-contain max-h-[70px] max-w-[100px]"
                            quality={90}
                          />
                        </div>
                      ))}
                    </AnimatedSection>
                  </div>
                </AnimatedSection>
              </AnimatedSection>
            </AnimatedSection>
          </div>
        </AnimatedSection>
      }
      fallbackContent={
        <section className="overflow-hidden w-full h-full">
          <div className="overflow-hidden relative w-full h-full">
            <div className="absolute left-0 top-0 inset-0 w-full h-full -translate-y-[20%] xl:-translate-y-[0] z-10">
              <div className="h-full w-[200vw] xl:w-full -ml-[50vw] xl:ml-0">
                <BackgroundImage
                  assetId={
                    backgroundImage?.images_id?.image_file?.image?.id || ""
                  }
                  temp={
                    backgroundImage?.images_id?.image_file?.image?.id &&
                    backgroundImage?.images_id?.image_file?.image
                      ?.filename_download
                      ? getReadableImageUrl(
                          backgroundImage.images_id.image_file.image.id,
                          backgroundImage.images_id.image_file.image
                            .filename_download
                        )
                      : "/images/8.jpg"
                  }
                  className="object-cover object-center"
                  quality={100}
                  priority
                />
              </div>
            </div>
            <div className="relative w-full h-full z-30">
              <div className="absolute left-0 top-0 w-full h-full inset-0 xl:hidden z-20">
                <div className="absolute z-10 top-[20%] lg:top-[35%] left-[-100%] w-[300%] h-[150%] bg-white rounded-full" />
              </div>
              <div className="relative w-full h-full z-30 pt-8 lg:pt-16 overflow-hidden">
                <div className="gl-container flex flex-col xl:items-start w-full h-full">
                  <div className="xl:w-[32%] w-full h-full">
                    <div className=" mx-auto xl:mx-0 xl:max-w-2xl space-y-7 xl:space-y-9 relative mt-[25vh] xl:mt-[5vh] 2xl:mt-[10vh] w-full h-full">
                      <div className="w-full h-full">
                        <div className="space-y-3 pb-3">
                          <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary">
                            {data?.heading?.text || "Meet\nDr. Bradford Bader"}
                          </h2>
                          <p className="text-primary/90 font-greycliff text-base xl:text-2xl leading-loose tracking-tight">
                            {data?.subheading?.text ||
                              "Facial Plastic Surgeon in Plano"}
                          </p>
                        </div>
                        <div
                          className="text-[1rem] font-normal leading-normal font-greycliff tracking-tight text-primary [&>p]:py-2"
                          dangerouslySetInnerHTML={sanitizeHTML(
                            data?.text?.text || ""
                          )}
                        />
                        <div className="mt-4 justify-center flex">
                          <Link
                            href="/about/meet-dr-bader/"
                            className="w-full sm:w-auto px-12 py-3 bg-primary rounded-[3rem] text-white text-xl font-normal font-greycliff text-center"
                          >
                            Meet Dr. Bader
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div className="w-full flex items-center justify-between xl:w-auto xl:absolute xl:bottom-[30%] xl:right-32">
                      {awardImages?.map((award, index) => (
                        <div
                          key={index}
                          className={`flex items-center ${
                            index > 0 ? "xl:ml-16" : ""
                          }`}
                        >
                          <Image
                            src={
                              award.images_id.image_file.image.id &&
                              award.images_id.image_file.image.filename_download
                                ? getReadableImageUrl(
                                    award.images_id.image_file.image.id,
                                    award.images_id.image_file.image
                                      .filename_download
                                  )
                                : ""
                            }
                            alt={award.images_id.alt}
                            width={100}
                            height={70}
                            className="object-contain max-h-[70px] max-w-[100px]"
                            quality={90}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      }
    />
  );
}
